import React, { useState, useEffect, useCallback } from "react"
import clsx from "clsx"
import {
	IconButton,
	Typography,
	Avatar,
	Badge,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Grid,
	Input
} from "@material-ui/core"

import {
	Search as SearchIcon,
	PersonAdd as AddContactIcon
} from "@material-ui/icons"

import { Plus as PlusIcon } from "lucide-react"

import { useChatGlobalStateStore } from "@/store/ChatGlobalState"
import useCustomMemo from "@/hooks/useCustomMemo"
import useDidMount from "@/hooks/useDidMount"
import useDebounce from "@/hooks/useDebounce"
import useStartChat from "@/hooks/useStartChat"

import { channelInfo } from "@/utils/channel"
import {
	getPhoneContact,
	getPhoneNumber
} from "@/utils/contact"

import useContactDisplay from "@/hooks/useContactDisplay"
import { Loading, AccessibleDrawer, VirtualizedList } from "@/components"
import AddClientDialog from "@/components/AddClientDialog"
import ContactGroupPermissionGate from "@/components/PermissionGate/ContactGroupPermissionGate"

import StartChatDialog from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/NewChatButton/StartChatDialog"
import HeaderDrawer from "@/pages/Attendance/Chat/HeaderDrawer"

import { Client, Contact } from "@/protocols/clientCatalog"

import ChatListSkeleton from "@/skeletons/ChatList"

import StorageService from "@/services/Storage"

import useCustomStyles from "@/styles/custom"
import useChatStyles from "@/pages/Attendance/Chat/styles"
import useSidebarDrawerStyles from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/styles"
import useStyles from "@/pages/Attendance/Chat/ChatListPanel/ChatListHeader/NewChatButton/styles"
import { colors } from "@/styles/colors"
import { useMobileNav } from "@/contexts/MobileNavContext"
import { isSmallScreen } from "@/utils/checkDevice"

const NewChatButton: React.FC = () => {
	const [openDrawer, setOpenDrawer] = useState(false)
	const [searchFilter, setSearchFilter] = useState("")
	const [openStartChatDialog, setOpenStartChatDialog] = useState(false)
	const [selectedClient, setSelectedClient] = useState<Client>()

	const isSidebarCollapsed = StorageService.get<boolean>(StorageService.reservedKeys.INBOX_SIDEBAR_COLLAPSED) ?? false

	const classes = useStyles()
	const chatClasses = useChatStyles()
	const customClasses = useCustomStyles()
	const sidebarDrawerClasses = useSidebarDrawerStyles({ isCollapsed: isSidebarCollapsed })
	const chatGlobalStateStore = useChatGlobalStateStore()
	const { startChat } = useStartChat()
	const { getDisplayName, getDisplayPhoneNumber } = useContactDisplay()
	const { newChatDrawerOpenerRef } = useMobileNav()

	const handleOpenDrawer = useCallback(() => {
		setOpenDrawer(true)
	}, [])

	const handleCloseDrawer = () => {
		setOpenDrawer(false)
		setSelectedClient(undefined)
	}

	useEffect(() => {
		newChatDrawerOpenerRef.current = handleOpenDrawer
	}, [handleOpenDrawer])

	const handleChangeSearchFilter = (value: string) => {
		setSearchFilter(value)
	}

	const handleOpenStartChatDialog = (client: Client) => {
		setOpenStartChatDialog(true)
		setSelectedClient(client)
	}

	const handleCloseStartChatDialog = () => {
		setOpenStartChatDialog(false)
		setSelectedClient(undefined)
	}

	const eventKeyDownHandler = (event: KeyboardEvent) => {
		const { key } = event

		if (key === "Escape") {
			handleCloseDrawer()
		}
	}

	const onStartChat = async (client: Client) => {
		// I know there will be an whatsapp contact because only clients with
		// at least one whatsapp contact appear on the list
		const contact = getPhoneContact(client) as Contact

		const errors = await startChat(client.id, contact.id)

		if (!errors) {
			handleCloseDrawer()
			handleCloseStartChatDialog()
		}

		return errors
	}

	useDebounce(() => {
		if (!chatGlobalStateStore.client.loadingFromServer) {
			if (!searchFilter) {
				chatGlobalStateStore.client.loadCountOrSearchMatchFromServer()
				chatGlobalStateStore.client.setupSearchClients([], chatGlobalStateStore.client.count)
			} else {
				chatGlobalStateStore.client.loadCountOrSearchMatchFromServer(searchFilter)
			}
		}
	}, searchFilter, 1250)

	useDidMount(() => {
		chatGlobalStateStore.client.loadCountOrSearchMatchFromServer()

		window.addEventListener(
			"keydown",
			eventKeyDownHandler,
			{ capture: true }
		)
	})

	const ContactListItem = (client: Client) => {
		// I can guarantee that all clients have whatsapp because is filtered when getting clientCatalog
		const contactPhoneNumber = getPhoneNumber(client)

		return (
			<ListItem
				button
				onClick={() => handleOpenStartChatDialog(client)}
			>
				<ListItemAvatar>
					<Badge
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						badgeContent={(
							<Grid
								container
								className={classes.badgeContainer}
							>
								{channelInfo.whatsapp?.icon || null}
							</Grid>
						)}
					>
						<Avatar src={client.picture_url} />
					</Badge>
				</ListItemAvatar>

				<ListItemText
					primary={getDisplayName(client.nickname, contactPhoneNumber)}
					primaryTypographyProps={{
						variant: "h2",
						color: "textPrimary"
					}}
					secondary={(
						<Grid
							container
							direction="column"
						>
							<Typography
								variant="caption"
								color="textPrimary"
								className={`${classes.chatDescription} ${customClasses.inlineText}`}
							>
								{getDisplayPhoneNumber(contactPhoneNumber)}
							</Typography>
						</Grid>
					)}
				/>
			</ListItem>
		)
	}

	const CustomListItems = [
		<ListItem
			key="contact-count"
		>
			<ListItemText
				primary={
					searchFilter
						? `${chatGlobalStateStore.client.count || 0} contato${chatGlobalStateStore.client.count > 1 ? "s" : ""} encontrado${chatGlobalStateStore.client.count > 1 ? "s" : ""}`
						: `Você possui um total de ${chatGlobalStateStore.client.count} contato${chatGlobalStateStore.client.count !== 1 ? "s" : ""}, utilize a busca para encontrar um contato específico.`
				}
				primaryTypographyProps={{
					variant: "body2",
					color: "textPrimary"
				}}
			/>
		</ListItem>,
		<Grid
			item
			className={classes.newContactWrapper}
			key="add-contact-dialog"
		>
			<ContactGroupPermissionGate action="create">
				<AddClientDialog
					nickname={searchFilter}
				>
					<ListItem
						button
						className={classes.newContactListItem}
					>
						<ListItemAvatar>
							<Avatar className={classes.addContactAvatar}>
								<AddContactIcon />
							</Avatar>
						</ListItemAvatar>

						<ListItemText
							primary="Novo contato"
							primaryTypographyProps={{
								variant: "h2",
								color: "textPrimary"
							}}
						/>
					</ListItem>
				</AddClientDialog>
			</ContactGroupPermissionGate>
		</Grid>
	]

	return useCustomMemo(() => (
		<>
			{!isSmallScreen && (
				<IconButton
					onClick={handleOpenDrawer}
					className={clsx(classes.iconButton, classes.newChatTriggerButton)}
					size="small"
				>
					<PlusIcon size={22} color={colors.gray[0]} />
				</IconButton>
			)}

			<AccessibleDrawer
				id="new-chat-drawer"
				anchor="left"
				open={openDrawer}
				variant="persistent"
				onClose={handleCloseDrawer}
				onMobileBackButtonPress={handleCloseDrawer}
				classes={{
					paper: clsx(chatClasses.drawerPaper, !isSmallScreen && sidebarDrawerClasses.drawerPaperOffset)
				}}
			>
				<Grid
					container
				>
					<Grid item xs={12}>
						<HeaderDrawer
							title="Iniciar atendimento"
							onClose={handleCloseDrawer}
						/>
					</Grid>

					<Grid item xs={12} className={classes.searchInputContainer} >
						<Input
							fullWidth
							startAdornment={<SearchIcon />}
							className={classes.input}
							placeholder="Buscar nos contatos"
							value={searchFilter}
							onChange={({ target }) => handleChangeSearchFilter(target.value)}
						/>
					</Grid>

					<Grid item xs={12}>
						<Loading
							loading={chatGlobalStateStore.client.loadingFromServer}
							customLoadingElement={<ChatListSkeleton />}
						>
							<Grid
								container
							>
								<VirtualizedList
									containerWidth={window.innerWidth}
									containerHeight={window.innerHeight}
									className={`${classes.list} ${customClasses.scrollBar}`}
									itemSize={63}
									itemCount={chatGlobalStateStore.client.listSearch.length + CustomListItems.length}
									itemRender={({ index, style }) => {
										const client = chatGlobalStateStore.client.listSearch[index - CustomListItems.length]

										return (
											<Grid
												style={style}
												container
												key={index}
											>
												{index > 1 ? (
													ContactListItem(client)
												) : (
													CustomListItems[index]
												)}
											</Grid>
										)
									}}
								/>
							</Grid>
						</Loading>
					</Grid>
				</Grid>
			</AccessibleDrawer>

			{
				(
					<AddClientDialog
						onAdd={chatGlobalStateStore.client.loadCountOrSearchMatchFromServer}
						nickname={searchFilter}
					/>
				)
			}

			{selectedClient && (
				<StartChatDialog
					client={selectedClient}
					open={openStartChatDialog}
					onClose={handleCloseStartChatDialog}
					onStartChat={onStartChat}
				/>
			)}
		</>
	), [
		chatGlobalStateStore.client.count,
		searchFilter,
		openDrawer,
		openStartChatDialog,
		chatGlobalStateStore.client.loadingFromServer
	])
}

export default NewChatButton
